/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React from 'react'
import { useIntl } from 'react-intl'
import { useNavigate } from 'react-router-dom'

import {
  EuiFlexItem,
  EuiFlexGroup,
  EuiSpacer,
  EuiText,
  EuiImage,
  useEuiTheme,
  EuiButtonEmpty,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'

// eslint-disable-next-line import/no-restricted-paths
import illustrationGlobalPeopleNetwork128 from '@/files/illustration-global-people-network-128.svg'
// eslint-disable-next-line import/no-restricted-paths
import { createOrganizationUrl } from '@/apps/userconsole/urls'

import { messages } from '../messages'

const EmptyOrganizationsPrompt = ({
  size,
  showCreateOrganizationButton,
}: {
  size: 's' | 'm'
  showCreateOrganizationButton?: boolean
}) => {
  const { formatMessage } = useIntl()
  const navigate = useNavigate()
  const { euiTheme } = useEuiTheme()
  return (
    <EuiFlexGroup direction='column' justifyContent='space-between' gutterSize='s'>
      <EuiSpacer size='l' />
      <EuiFlexItem grow={false}>
        <EuiImage
          src={illustrationGlobalPeopleNetwork128}
          alt={formatMessage({ ...messages.noOrganizationImageAlt })}
          size={size}
        />
      </EuiFlexItem>
      <EuiFlexItem grow={false}>
        <EuiText size={size} textAlign='center' color={euiTheme.colors.textHeading}>
          <strong>
            <FormattedMessage {...messages.noOrganizationFound} />
          </strong>
        </EuiText>
      </EuiFlexItem>
      <EuiFlexItem grow={false}>
        <EuiText
          size={size}
          textAlign='center'
          color={euiTheme.colors.textSubdued}
          style={{ maxWidth: '400px', alignSelf: 'center' }}
        >
          <p style={{ marginBlockEnd: '0px' }}>
            <FormattedMessage {...messages.noOrganizationDescription} />
          </p>
          <p>
            <FormattedMessage {...messages.noOrganizationCta} />
          </p>
        </EuiText>
      </EuiFlexItem>
      <EuiSpacer size='xs' />
      {showCreateOrganizationButton && (
        <EuiFlexItem grow={false}>
          <EuiButtonEmpty flush='both' onClick={() => navigate(createOrganizationUrl())}>
            <FormattedMessage {...messages.createOrganization} />
          </EuiButtonEmpty>
        </EuiFlexItem>
      )}
      <EuiSpacer size='l' />
    </EuiFlexGroup>
  )
}

export default EmptyOrganizationsPrompt
